import type {
  FormatOptions,
  FormatResult,
  LintOptions,
  LintReport,
  RepairOptions,
  RepairResult,
} from '@jsonfmt/core';

/**
 * The four service INTERFACES, and only the interfaces.
 *
 * Why a separate file from `index.ts`, which docs/ARCHITECTURE.md §4.7 names: `index.ts`
 * has to import the concrete implementations in order to build them, and those touch
 * `navigator`, `localStorage` and `Worker`. `CommandContext` in `src/features/types.ts`
 * holds a `Services`, and every pure `.ts` under `src/features` is compiled by `tsconfig.core.json` —
 * `lib: ["ES2022"]`, `types: []` — so anything reachable from `features/types.ts` must be
 * DOM-free. Splitting the pure types out is what keeps gate 1 honest; `index.ts` re-exports
 * every name below, so `@/services` remains the public surface.
 *
 * THE ADMISSION RULE (ADR-0015): something becomes a service only if a scenario has to READ
 * FROM THE OUTSIDE WORLD or HAND WORK TO ANOTHER THREAD. Everything a scenario merely
 * writes outward comes back as data. That rule is what deletes `NotificationPort` and
 * `TranslationPort`; do not add a fifth service without arguing against it in an ADR.
 */

export interface ClipboardService {
  /** `false` in Firefox, where `readText` is not granted to a page: Paste is not rendered. */
  readonly canRead: boolean;
  /** `false` outside a secure context. */
  readonly canWrite: boolean;
  read(): Promise<string>;
  write(text: string): Promise<void>;
}

/**
 * The complete set of keys this application writes. Nothing about the user's document is ever
 * persisted (ADR-0023).
 *
 * DECLARED HERE, NAMED IN `src/app/config.ts`. The composition root is the readable index of
 * the application's tunables, but `services/` and `ui/` both write these keys and gate 2
 * denies both of them `@/app/*` — so a declaration there would be a decoy that three layers
 * spell as literals instead. It was: `services/theme.ts` had its own `'jsonfmt.theme'` and
 * `ui/i18n/I18nProvider.tsx` had two bare `'jsonfmt.locale'`s, while the "single home" in
 * `config.ts` had no importer at all and could be edited with no effect and no gate objecting.
 * This module is the lowest one every party can import, and it already owned the union.
 */
export const STORAGE_KEYS = {
  theme: 'jsonfmt.theme',
  locale: 'jsonfmt.locale',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

export interface StorageService {
  /**
   * `parse` validates the raw value and returns `null` when it is not what was expected;
   * `fallback` is returned then, and also when the read throws — which it does in Safari's
   * private mode, on ACCESS, rather than returning null.
   */
  get<T>(key: StorageKey, parse: (raw: unknown) => T | null, fallback: T): T;
  set(key: StorageKey, value: unknown): void;
  remove(key: StorageKey): void;
}

export type ThemePreference = 'light' | 'dark' | 'system';

/**
 * What the theme is before anyone has chosen: follow the operating system.
 *
 * Same reasoning as `STORAGE_KEYS` — `services/theme.ts` reads it as the fallback of its
 * `storage.get`, and `src/app/config.ts` names it.
 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'system';

export interface ThemeService {
  getPreference(): ThemePreference;
  setPreference(p: ThemePreference): void;
  getResolved(): 'light' | 'dark';
  /** Also fires when the OS theme changes while the preference is `system`. */
  subscribe(listener: () => void): () => void;
}

export interface AnalyzerService {
  /** Exactly one analysis method. Worker or inline is the implementation's own business. */
  lint(source: string, options?: Partial<LintOptions>): Promise<LintReport>;
  format(source: string, options?: Partial<FormatOptions>): Promise<FormatResult>;
  repair(source: string, options?: Partial<RepairOptions>): Promise<RepairResult>;
  dispose(): void;
}

export interface Services {
  readonly clipboard: ClipboardService;
  readonly storage: StorageService;
  readonly theme: ThemeService;
  readonly analyzer: AnalyzerService;
}
